import { devLog } from './devLog';

export const API_BASE_URL = 'https://irpapi.selficai.com/api/v1';
const TENANT_ID = process.env.REACT_APP_TENANT_ID || 'tenant123';

// JWT Token - In production, this would come from login/auth
// For development, using a static token
const getAuthToken = () => {
  // In production, you would get this from localStorage or auth context
  // return localStorage.getItem('jwt_token');

  // For development
  return 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJ1c2VySWQiOiJ0ZW5hbnQxMjMiLCJ0ZW5hbnRJZCI6InRlbmFudDEyMyIsImlhdCI6MTYxNjIzOTAyMn0.SflKxwRJSMeKKF2QT4fwpMeJf36POk6yJV_adQssw5c';
};

// API utility functions for interacting with the backend

// Helper function to extract error message from API response
// Handles multiple error formats consistently
export const extractErrorMessage = (result) => {
  // Check for nested error format: { error: { message: "..." } }
  if (result.error && result.error.message) {
    return result.error.message;
  }

  // Check for FastAPI format: { detail: "..." }
  if (result.detail) {
    return result.detail;
  }

  // Check for simple format: { message: "..." }
  if (result.message) {
    return result.message;
  }

  // Check if result is a string
  if (typeof result === 'string') {
    return result;
  }

  // Default fallback
  return 'Unknown error occurred';
};

// Generate slug from job title
export const generateSlug = (text) => {
  return text
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '') // Remove special characters
    .replace(/\s+/g, '-')      // Replace spaces with hyphens
    .replace(/-+/g, '-');      // Replace multiple hyphens with single hyphen
};

// Create a new role/job
export const createRole = async (jobData) => {
  const roleId = generateSlug(jobData.title);

  const payload = {
    tenantId: TENANT_ID,
    roleId: roleId,
    name: jobData.title,
    description: jobData.description,
    totalApplications: 0,
    totalPositions: 1,
    status: "Open",
    jd_path: jobData.requirements,
    scoring_weights: jobData.scoring_weights || null
  };

  try {
    const response = await fetch(`${API_BASE_URL}/roles/${TENANT_ID}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${getAuthToken()}`
      },
      body: JSON.stringify(payload)
    });

    const data = await response.json();

    if (!response.ok) {
      const errorMessage = extractErrorMessage(data);
      throw new Error(errorMessage);
    }

    return {
      success: true,
      data: data,
      role: {
        id: data.data.roleId,
        roleId: data.data.roleId,
        title: jobData.title,
        description: jobData.description,
        requirements: jobData.requirements || '',
        createdAt: new Date().toLocaleString(),
        cvCount: 0,
        status: 'Open',
        totalPositions: 1,
        totalApplications: 0
      }
    };
  } catch (error) {
    devLog.error('Error creating role:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

// Sort jobs by status priority: Open > On Hold > Closed
export const sortJobsByStatus = (jobs) => {
  const statusPriority = {
    'Open': 1,
    'On Hold': 2,
    'Closed': 3
  };

  return [...jobs].sort((a, b) => {
    const priorityA = statusPriority[a.status] || 999;
    const priorityB = statusPriority[b.status] || 999;
    return priorityA - priorityB;
  });
};

// Get all roles for tenant with optional status filter
export const getRoles = async (statusFilter = null) => {
  try {
    let url = `${API_BASE_URL}/roles?tenantId=${TENANT_ID}`;

    // Add status filter if provided
    if (statusFilter && statusFilter !== 'All') {
      url += `&status=${statusFilter}`;
    }

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${getAuthToken()}`
      }
    });

    const data = await response.json();

    if (!response.ok) {
      const errorMessage = extractErrorMessage(data);
      throw new Error(errorMessage);
    }

    // Transform API response to our format
    let roles = data.data.roles.map(role => ({
      id: role.roleId,
      _id: role._id,
      roleId: role.roleId,
      title: role.name,
      description: role.description,
      requirements: role.jd_path || '',
      createdAt: role.createdAt ? new Date(role.createdAt).toLocaleString() : new Date().toLocaleString(),
      cvCount: role.totalApplications || 0,
      status: role.status,
      totalPositions: role.totalPositions || 1,
      totalApplications: role.totalApplications || 0
    }));

    // Sort by status if showing all (Open first, Closed last)
    if (!statusFilter || statusFilter === 'All') {
      roles = sortJobsByStatus(roles);
    }

    return {
      success: true,
      data: {
        roles: roles,
        totalRoles: data.data.totalRoles,
        tenantId: data.data.tenantId
      }
    };
  } catch (error) {
    devLog.error('Error fetching roles:', error);
    return {
      success: false,
      error: error.message,
      data: { roles: [], totalRoles: 0 }
    };
  }
};

// Soft delete a role by updating its status to "Closed"
export const closeRole = async (roleData) => {
  const payload = {
    tenantId: TENANT_ID,
    roleId: roleData.roleId,
    name: roleData.title,
    description: roleData.description,
    totalApplications: roleData.totalApplications || 0,
    totalPositions: roleData.totalPositions || 1,
    status: "Closed", // Update status to Closed
    jd_path: roleData.requirements
  };

  try {
    const response = await fetch(`${API_BASE_URL}/roles/${TENANT_ID}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${getAuthToken()}`
      },
      body: JSON.stringify(payload)
    });

    const data = await response.json();

    if (!response.ok) {
      const errorMessage = extractErrorMessage(data);
      throw new Error(errorMessage);
    }

    return {
      success: true,
      data: data
    };
  } catch (error) {
    devLog.error('Error closing role:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

// Hard delete a role and all related data
export const deleteRole = async (roleId) => {
  try {
    const response = await fetch(`${API_BASE_URL}/roles/${encodeURIComponent(roleId)}?tenantId=${TENANT_ID}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${getAuthToken()}`
      }
    });

    const data = await response.json();

    if (!response.ok) {
      const errorMessage = extractErrorMessage(data);
      throw new Error(errorMessage);
    }

    return {
      success: true,
      data: data
    };
  } catch (error) {
    devLog.error('Error deleting role:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

// Update role status
export const updateRoleStatus = async (roleData, newStatus) => {
  const payload = {
    tenantId: TENANT_ID,
    roleId: roleData.roleId,
    name: roleData.title,
    description: roleData.description,
    totalApplications: roleData.totalApplications || 0,
    totalPositions: roleData.totalPositions || 1,
    status: newStatus, // Open, On Hold, or Closed
    jd_path: roleData.requirements
  };

  try {
    const response = await fetch(`${API_BASE_URL}/roles/${TENANT_ID}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${getAuthToken()}`
      },
      body: JSON.stringify(payload)
    });

    const data = await response.json();

    if (!response.ok) {
      const errorMessage = extractErrorMessage(data);
      throw new Error(errorMessage);
    }

    return {
      success: true,
      data: data
    };
  } catch (error) {
    devLog.error('Error updating role status:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

// AI Agent APIs

// Get CV analyses for a specific role
export const getRoleAnalyses = async (roleId) => {
  try {
    const url = `${API_BASE_URL}/roles/${encodeURIComponent(roleId)}/analyses?tenantId=${TENANT_ID}`;

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${getAuthToken()}`
      }
    });

    if (!response.ok) {
      if (response.status === 404) {
        return { success: true, data: [] };
      }
      throw new Error('Failed to fetch role analyses');
    }

    const data = await response.json();
    return { success: true, data: Array.isArray(data) ? data : [] };
  } catch (error) {
    devLog.error('Error fetching role analyses:', error);
    return { success: false, error: error.message, data: [] };
  }
};

// Get candidate profile for a specific role and email
export const getCandidateProfile = async (roleId, email) => {
  try {
    const url = `${API_BASE_URL}/roles/${encodeURIComponent(roleId)}/candidates/${encodeURIComponent(email)}?tenantId=${TENANT_ID}`;

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${getAuthToken()}`
      }
    });

    if (!response.ok) {
      throw new Error('Failed to fetch candidate profile');
    }

    const data = await response.json();
    return { success: true, data };
  } catch (error) {
    devLog.error('Error fetching candidate profile:', error);
    return { success: false, error: error.message };
  }
};

// Get multiple candidate profiles in batch
export const getCandidateProfilesBatch = async (roleId, emails) => {
  try {
    if (!emails || emails.length === 0) {
      return { success: true, data: {} };
    }

    const emailsParam = emails.join(',');
    const url = `${API_BASE_URL}/roles/${encodeURIComponent(roleId)}/candidates/batch?tenantId=${TENANT_ID}&emails=${encodeURIComponent(emailsParam)}`;

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${getAuthToken()}`
      }
    });

    if (!response.ok) {
      if (response.status === 404) {
        return { success: true, data: {} };
      }
      throw new Error('Failed to fetch candidate profiles');
    }

    const data = await response.json();
    return { success: true, data: data || {} };
  } catch (error) {
    devLog.error('Error fetching candidate profiles batch:', error);
    return { success: false, error: error.message, data: {} };
  }
};

// Get candidate screenings/interview results
export const getCandidateScreenings = async (roleId, email) => {
  try {
    const url = `${API_BASE_URL}/roles/${encodeURIComponent(roleId)}/candidates/${encodeURIComponent(email)}/screenings?tenantId=${TENANT_ID}`;

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${getAuthToken()}`
      }
    });

    if (response.status === 404) {
      return { success: true, data: [] };
    }

    if (!response.ok) {
      throw new Error('Failed to fetch candidate screenings');
    }

    const data = await response.json();
    return { success: true, data: Array.isArray(data) ? data : [] };
  } catch (error) {
    devLog.error('Error fetching candidate screenings:', error);
    return { success: true, data: [] }; // Return empty array on error
  }
};

// Send interview email to candidate
export const sendInterviewEmail = async (roleId, email, interviewLink, interviewType, candidateName, jobTitle) => {
  try {
    const url = `${API_BASE_URL}/roles/${encodeURIComponent(roleId)}/candidates/${encodeURIComponent(email)}/send-email?tenantId=${TENANT_ID}`;

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${getAuthToken()}`
      },
      body: JSON.stringify({
        interviewLink,
        interviewType,
        candidateName,
        jobTitle
      })
    });

    const data = await response.json();

    if (!response.ok) {
      const errorMessage = extractErrorMessage(data);
      throw new Error(errorMessage);
    }

    return { success: true, data };
  } catch (error) {
    devLog.error('Error sending interview email:', error);
    return { success: false, error: error.message };
  }
};

// Save interview link for a candidate
export const saveInterviewLink = async (roleId, email, interviewLink, interviewType, linkId) => {
  try {
    const url = `${API_BASE_URL}/roles/${encodeURIComponent(roleId)}/candidates/${encodeURIComponent(email)}/interview-link?tenantId=${TENANT_ID}`;

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${getAuthToken()}`
      },
      body: JSON.stringify({
        interviewLink,
        interviewType,
        linkId
      })
    });

    if (!response.ok) {
      throw new Error('Failed to save interview link');
    }

    const data = await response.json();
    return { success: true, data };
  } catch (error) {
    devLog.error('Error saving interview link:', error);
    return { success: false, error: error.message };
  }
};

// Interview Questions APIs

// Add a question to a role
export const addQuestionToRole = async (roleId, questionData) => {
  try {
    const url = `${API_BASE_URL}/roles/${encodeURIComponent(roleId)}/questions?tenantId=${TENANT_ID}`;

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${getAuthToken()}`
      },
      body: JSON.stringify(questionData)
    });

    const data = await response.json();

    if (!response.ok) {
      const errorMessage = extractErrorMessage(data);
      throw new Error(errorMessage);
    }

    return { success: true, data };
  } catch (error) {
    devLog.error('Error adding question:', error);
    return { success: false, error: error.message };
  }
};

// Get all questions for a role
export const getRoleQuestions = async (roleId) => {
  try {
    const url = `${API_BASE_URL}/roles/${encodeURIComponent(roleId)}/questions?tenantId=${TENANT_ID}`;

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${getAuthToken()}`
      }
    });

    if (!response.ok) {
      if (response.status === 404) {
        return { success: true, data: { questions: [], totalQuestions: 0 } };
      }
      throw new Error('Failed to fetch questions');
    }

    const data = await response.json();
    return { success: true, data: data.data };
  } catch (error) {
    devLog.error('Error fetching questions:', error);
    return { success: false, error: error.message, data: { questions: [], totalQuestions: 0 } };
  }
};

// Delete a question from a role
export const deleteQuestion = async (roleId, questionId) => {
  try {
    const url = `${API_BASE_URL}/roles/${encodeURIComponent(roleId)}/questions/${encodeURIComponent(questionId)}?tenantId=${TENANT_ID}`;

    const response = await fetch(url, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${getAuthToken()}`
      }
    });

    const data = await response.json();

    if (!response.ok) {
      // Handle 404 errors specifically
      if (response.status === 404) {
        const errorMessage = extractErrorMessage(data);
        return {
          success: false,
          error: errorMessage || 'Question or role not found',
          notFound: true
        };
      }

      const errorMessage = extractErrorMessage(data);
      throw new Error(errorMessage);
    }

    // Success response: { status: "success", message: "...", data: { questionId: "...", totalQuestions: ... } }
    return { success: true, data };
  } catch (error) {
    devLog.error('Error deleting question:', error);
    return { success: false, error: error.message };
  }
};
